import { POPOVER_REGEX } from '@examdojo/tiptap-popover/extension/popover.model';
import type { JSONContent } from '@tiptap/core';
import { v4 as uuid } from 'uuid';
import { ContentBlock, PopoverData } from '../../../model';
import { convertBlocksToTiptapNodes } from '../../blocks-to-tiptap.converter';
import type { ConverterContext } from '../../blocks-to-tiptap.converter';
import { convertTiptapNodesToBlocks } from '../../tiptap-to-blocks.converter';

export interface PopoverMarkAttrs {
  id?: string;
  jsonContent?: JSONContent;
  linkUrl?: string;
}

/**
 * Recursively processes a node to convert popover markers to popover marks.
 */
export function processNodeForPopovers(
  node: JSONContent,
  popovers?: Record<string, PopoverData>,
  context?: ConverterContext,
): JSONContent {
  // Process content array for popover markers
  if (node.content) {
    node = {
      ...node,
      content: processContentForPopovers(node.content, popovers, context),
    };
  }

  return node;
}

/**
 * Processes an array of content nodes, converting popover markers to popover marks.
 */
function processContentForPopovers(
  content: JSONContent[],
  popovers?: Record<string, PopoverData>,
  context?: ConverterContext,
): JSONContent[] {
  const result: JSONContent[] = [];

  for (const node of content) {
    if (node.type === 'text' && node.text) {
      const parts = splitTextWithPopovers(node.text, node.marks, popovers, context);
      result.push(...parts);
    } else if (node.content) {
      // Recursively process nested content
      result.push({
        ...node,
        content: processContentForPopovers(node.content, popovers, context),
      });
    } else {
      result.push(node);
    }
  }

  return result;
}

/**
 * Splits a text string containing popover markers into text nodes with popover marks.
 */
function splitTextWithPopovers(
  text: string,
  marks?: JSONContent['marks'],
  popovers?: Record<string, PopoverData>,
  context?: ConverterContext,
): JSONContent[] {
  const result: JSONContent[] = [];
  let lastIndex = 0;

  // Reset regex state
  POPOVER_REGEX.lastIndex = 0;

  let match;
  while ((match = POPOVER_REGEX.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      const beforeText = text.slice(lastIndex, match.index);
      result.push({ type: 'text', text: beforeText, ...(marks && { marks }) });
    }

    const popoverId = match[1];
    const popoverText = match[2];
    const popoverData = popovers?.[popoverId];

    // Convert popover content to JSONContent if available
    const jsonContent = convertPopoverContentToJson(popoverData?.content, context);

    // Create popover mark
    const popoverMark: JSONContent['marks'] = [
      {
        type: 'popover',
        attrs: {
          id: popoverId,
          jsonContent,
          ...(popoverData?.linkUrl && { linkUrl: popoverData.linkUrl }),
        },
      },
    ];

    // Combine with existing marks
    const combinedMarks = marks ? [...marks, ...popoverMark] : popoverMark;

    // Add text with popover mark
    result.push({
      type: 'text',
      text: popoverText,
      marks: combinedMarks,
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    result.push({ type: 'text', text: remainingText, ...(marks && { marks }) });
  }

  // If no popovers were found, return original text node
  if (result.length === 0) {
    result.push({ type: 'text', text, ...(marks && { marks }) });
  }

  return result;
}

/**
 * Converts popover content (ContentBlock[]) to JSONContent for storing in popover marks.
 */
function convertPopoverContentToJson(content?: ContentBlock[], context?: ConverterContext): JSONContent | null {
  if (!content || content.length === 0) {
    return null;
  }

  const nodes = convertBlocksToTiptapNodes(content, { ...context, popover: true });

  return {
    type: 'doc',
    content: nodes,
  };
}

/**
 * Recursively converts popover marks back to {{popover:id}}text{{/popover}} markers.
 * Populates the popoversMap with extracted popover data.
 */
export function convertPopoversToMarkers(
  node: JSONContent,
  popoversMap: Record<string, PopoverData>,
  context?: ConverterContext,
): JSONContent {
  // Process content array for popover marks
  if (node.content) {
    return {
      ...node,
      content: convertPopoversInContent(node.content, popoversMap, context),
    };
  }

  return node;
}

/**
 * Processes content array, converting text nodes with popover marks to marker syntax.
 */
function convertPopoversInContent(
  content: JSONContent[],
  popoversMap: Record<string, PopoverData>,
  context?: ConverterContext,
): JSONContent[] {
  const result: JSONContent[] = [];

  for (const node of content) {
    if (node.type === 'text' && node.text && node.marks) {
      const popoverMark = node.marks.find((mark) => mark.type === 'popover');

      if (popoverMark) {
        const attrs = popoverMark.attrs as PopoverMarkAttrs | undefined;
        const popoverId = attrs?.id ?? uuid();
        const jsonContent = attrs?.jsonContent;
        const linkUrl = attrs?.linkUrl;

        // Convert JSONContent to ContentBlocks if available
        const contentBlocks = convertJsonToPopoverContent(jsonContent, context);

        // Store popover data in the map
        popoversMap[popoverId] = {
          content: contentBlocks,
          ...(linkUrl && { linkUrl }),
        };

        // Remove popover mark and wrap text with marker syntax
        const otherMarks = node.marks.filter((mark) => mark.type !== 'popover');
        const markedText = `{{popover:${popoverId}}}${node.text}{{/popover}}`;

        result.push({
          type: 'text',
          text: markedText,
          ...(otherMarks.length > 0 && { marks: otherMarks }),
        });
      } else {
        result.push(node);
      }
    } else if (node.content) {
      // Recursively process nested content
      result.push(convertPopoversToMarkers(node, popoversMap, context));
    } else {
      result.push(node);
    }
  }

  return result;
}

/**
 * Converts JSONContent from popover marks back to ContentBlock[].
 */
function convertJsonToPopoverContent(jsonContent?: JSONContent, context?: ConverterContext): ContentBlock[] {
  if (!jsonContent || jsonContent.type !== 'doc' || !jsonContent.content) {
    return [];
  }

  return convertTiptapNodesToBlocks(jsonContent.content, context);
}
